import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ArrowLeft, Edit, Heart, Users } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProfileView = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch both basic user data and detailed profile
      const [userResponse, detailedResponse] = await Promise.all([
        axios.get(`${API}/me`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/detailed-profile`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      // Merge user data with detailed profile
      const userData = userResponse.data;
      const detailedData = detailedResponse.data;
      
      // If detailed profile exists, merge it with user data
      if (detailedData.exists && detailedData.profile) {
        userData.bio = detailedData.profile.bio || userData.bio;
        userData.headline = detailedData.profile.headline || userData.headline;
        userData.interests = detailedData.profile.interests || userData.interests;
      }
      
      setProfile(userData);
      console.log('✅ Profile loaded with detailed data:', userData);
    } catch (error) {
      console.error('Error fetching profile:', error);
      setError('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen relative overflow-x-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-rose-900/50 to-pink-900/80"></div>
        
        <div className="relative z-10 min-h-screen flex items-center justify-center px-4">
          <Card className="w-full max-w-md bg-black/20 backdrop-blur-xl border border-white/10 shadow-2xl rounded-3xl">
            <CardContent className="text-center py-12">
              <div className="w-16 h-16 border-4 border-rose-400 border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
              <h2 className="text-2xl font-bold text-white mb-4">Loading Your Profile</h2>
              <p className="text-gray-300">Preparing your dating profile</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen relative overflow-x-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-rose-900/50 to-pink-900/80"></div>
        
        <div className="relative z-10 min-h-screen flex items-center justify-center px-4">
          <Card className="w-full max-w-md bg-black/20 backdrop-blur-xl border border-white/10 shadow-2xl rounded-3xl">
            <CardContent className="text-center py-12">
              <h2 className="text-2xl font-bold text-white mb-4">Profile Error</h2>
              <p className="text-gray-300 mb-6">{error}</p>
              <Button
                onClick={() => navigate('/profile')}
                className="bg-rose-600 hover:bg-rose-700 text-white"
              >
                Edit Profile
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Sophisticated Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-rose-900/50 to-pink-900/80"></div>
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-rose-400/20 via-transparent to-transparent"></div>
      
      <div className="relative z-10 min-h-screen px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex justify-between items-center mb-8">
            <Button
              onClick={() => navigate('/')}
              className="bg-white/10 hover:bg-white/20 text-white border border-white/20"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Button>
            
            <div className="text-center">
              <h1 className="text-3xl font-light text-white">Your Profile</h1>
              <p className="text-gray-300">Ready to find amazing connections</p>
            </div>
            
            <Button
              onClick={() => navigate('/profile')}
              className="bg-rose-600 hover:bg-rose-700 text-white"
            >
              <Edit className="h-4 w-4 mr-2" />
              Edit Profile
            </Button>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Profile Card */}
            <div className="lg:col-span-2">
              <Card className="bg-black/20 backdrop-blur-2xl border border-white/10 shadow-2xl rounded-3xl overflow-hidden">
                <CardHeader className="bg-gradient-to-r from-rose-500/20 via-pink-500/20 to-rose-600/20 backdrop-blur-md p-8">
                  <div className="flex items-center space-x-6">
                    {profile?.photo_url ? (
                      <div className="w-24 h-24 rounded-full overflow-hidden ring-4 ring-white/20 shadow-xl">
                        <img 
                          src={`${process.env.REACT_APP_BACKEND_URL}${profile.photo_url}`}
                          alt={profile?.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            // Fallback to initial if image fails to load
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                        <div className="w-24 h-24 bg-gradient-to-br from-rose-400 to-pink-500 rounded-full flex items-center justify-center text-white text-3xl font-bold" style={{display: 'none'}}>
                          {profile?.name?.charAt(0) || 'U'}
                        </div>
                      </div>
                    ) : (
                      <div className="w-24 h-24 bg-gradient-to-br from-rose-400 to-pink-500 rounded-full flex items-center justify-center text-white text-3xl font-bold ring-4 ring-white/20 shadow-xl">
                        {profile?.name?.charAt(0) || 'U'}
                      </div>
                    )}
                    <div>
                      <CardTitle className="text-3xl font-light text-white mb-2">
                        {profile?.name}, {profile?.age}
                      </CardTitle>
                      {profile?.headline && (
                        <p className="text-rose-300 text-lg font-medium mb-1">
                          {profile.headline}
                        </p>
                      )}
                      <p className="text-gray-300">{profile?.email}</p>
                      <Badge className="mt-2 bg-green-500/20 text-green-300 border-green-400/30">
                        Profile Complete
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent className="p-8 space-y-6">
                  {/* Bio Section */}
                  <div>
                    <h3 className="text-xl font-medium text-white mb-3">About Me</h3>
                    <div className="bg-white/5 backdrop-blur-md rounded-xl p-4 border border-white/10">
                      <p className="text-gray-300 leading-relaxed">
                        {profile?.bio || 'No bio provided yet.'}
                      </p>
                    </div>
                  </div>

                  {/* Interests */}
                  {profile?.interests && (
                    <div>
                      <h3 className="text-xl font-medium text-white mb-3">Interests</h3>
                      <div className="flex flex-wrap gap-2">
                        {(typeof profile.interests === 'string' 
                          ? profile.interests.split(',').map(i => i.trim()) 
                          : profile.interests || []
                        ).map((interest, idx) => (
                          <Badge 
                            key={idx} 
                            className="bg-rose-500/20 text-rose-300 border-rose-400/30"
                          >
                            {interest}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Matching Preferences Summary */}
                  {profile?.matching_preferences && (
                    <div>
                      <h3 className="text-xl font-medium text-white mb-3">Matching Preferences</h3>
                      <div className="grid md:grid-cols-2 gap-4">
                        {Object.entries(profile.matching_preferences).map(([category, prefs]) => (
                          <Card key={category} className="bg-white/5 border border-white/10">
                            <CardContent className="p-4">
                              <h4 className="text-white font-medium mb-2 capitalize">
                                {category.replace('_', ' ')}
                              </h4>
                              <div className="space-y-1 text-sm text-gray-300">
                                {Object.entries(prefs).map(([key, value]) => (
                                  <div key={key}>
                                    <span className="text-gray-400 capitalize">
                                      {key.replace('_', ' ')}:
                                    </span>{' '}
                                    <span>{Array.isArray(value) ? value.join(', ') : value}</span>
                                  </div>
                                ))}
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Action Panel */}
            <div className="space-y-6">
              <Card className="bg-black/20 backdrop-blur-2xl border border-white/10 shadow-2xl rounded-3xl">
                <CardContent className="p-6 text-center">
                  <h3 className="text-xl font-medium text-white mb-4">Ready to Connect?</h3>
                  <p className="text-gray-300 mb-6">
                    Your profile is complete and optimized for matching
                  </p>
                  
                  <div className="space-y-4">
                    <Button
                      onClick={() => navigate('/matches')}
                      className="w-full bg-gradient-to-r from-rose-500 to-pink-500 hover:from-rose-600 hover:to-pink-600 text-white py-4 text-lg"
                    >
                      <Users className="h-5 w-5 mr-2" />
                      Browse Matches
                    </Button>
                    
                    <Button
                      onClick={() => navigate('/account')}
                      className="w-full bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white py-3"
                    >
                      <Edit className="h-5 w-5 mr-2" />
                      Edit Profile
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Profile Completeness */}
              <Card className="bg-black/20 backdrop-blur-2xl border border-white/10 shadow-2xl rounded-3xl">
                <CardContent className="p-6">
                  <h4 className="text-white font-medium mb-4">Profile Strength</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300 text-sm">Completeness</span>
                      <span className="text-white font-medium">85%</span>
                    </div>
                    <div className="w-full bg-white/10 rounded-full h-3">
                      <div className="bg-gradient-to-r from-green-400 to-emerald-400 h-3 rounded-full transition-all duration-500" style={{width: '85%'}}></div>
                    </div>
                    <p className="text-xs text-gray-400">
                      Great profile! You're ready for quality matches.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileView;